import { GoogleAuthProvider, signInAnonymously, signInWithPopup, User } from 'firebase/auth';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FaGoogle } from 'react-icons/fa';
import { LuSparkles } from 'react-icons/lu';
import { Navigate } from 'react-router-dom';

import { completeSandbox, SANDBOX_EXPIRES_KEY, SandboxRateLimitError } from '../shared/api/sandbox';
import { verifyAuth } from '../shared/api/user';
import { auth } from '../shared/firebase/firebase';
import { ONBOARDING_DONE_KEY } from '../widgets/OnboardingFlow';
import SandboxProvisioning from '../widgets/SandboxProvisioning';

type GuestPhase = 'idle' | 'provisioning';

// A link like /auth?mode=demo (or /?mode=demo, which AuthGuard forwards here) skips the
// landing page entirely and drops the visitor straight into sandbox provisioning.
const isDemoModeRequested = () => new URLSearchParams(window.location.search).get('mode') === 'demo';

const AuthPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  // Lazy-init from the URL so a ?mode=demo visit renders straight into the provisioning
  // screen instead of flashing "Welcome back!" for a frame first.
  const [guestPhase, setGuestPhase] = useState<GuestPhase>(() => (isDemoModeRequested() ? 'provisioning' : 'idle'));
  const [guestError, setGuestError] = useState<string>('');
  const [authChecked, setAuthChecked] = useState(false);
  const demoAutoTriggeredRef = useRef(false);
  // usePipelineEvents opens a single, app-lifetime socket connection authenticated with
  // whichever uid is current the first time it mounts. It must not mount until the anonymous
  // sign-in has actually resolved (auth.currentUser holds the right uid) — otherwise it would
  // permanently bind the session's only socket to an empty/stale uid. This flips true right
  // after signInAnonymously resolves, gating when <SandboxProvisioning> (which uses the hook)
  // is allowed to mount.
  const [guestSignedIn, setGuestSignedIn] = useState(false);
  const [sandboxTriggered, setSandboxTriggered] = useState(false);
  const guestUserRef = useRef<User | null>(null);
  // Anonymous sign-in fires onAuthStateChanged before /api/sandbox/complete resolves;
  // this suppresses the auto-redirect until provisioning (+ loading state) is done.
  const provisioningRef = useRef(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user: User | null) => {
      if (user && !provisioningRef.current) {
        setUser(user);
      }
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  const handleGoogle = async () => {
    try {
      setLoading(true);
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const { user } = result;
      if (user) {
        await verifyAuth();
      }
      setUser(result.user);
    } catch (error: unknown) {
      setError((error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleTryGuest = useCallback(async () => {
    provisioningRef.current = true;
    setGuestError('');
    setGuestSignedIn(false);
    setSandboxTriggered(false);
    setGuestPhase('provisioning');
    try {
      const result = await signInAnonymously(auth);
      guestUserRef.current = result.user;
      // auth.currentUser now holds the anonymous user's uid — safe to mount the
      // pipeline-events listener from here on.
      setGuestSignedIn(true);
      const sandbox = await completeSandbox();
      // Sandbox comes pre-provisioned with a demo Jira integration — skip the
      // connect-Jira onboarding wizard entirely for this guest.
      localStorage.setItem(ONBOARDING_DONE_KEY, '1');
      localStorage.setItem(SANDBOX_EXPIRES_KEY, sandbox.sandboxExpiresAt);
      // The BE pipeline was just kicked off; SandboxProvisioning takes over from here
      // and calls handleSandboxReady once it's actually done (or a fallback elapses).
      setSandboxTriggered(true);
    } catch (error: unknown) {
      provisioningRef.current = false;
      setGuestPhase('idle');
      setGuestSignedIn(false);
      setGuestError(
        error instanceof SandboxRateLimitError ? error.message : 'Could not set up the sandbox. Please try again.',
      );
      // The sandbox never got provisioned — sign out so this anonymous account
      // doesn't linger as an authenticated-but-empty session on reload
      // (AuthGuard only checks for *any* Firebase user, anonymous included).
      try {
        await auth.signOut();
      } catch {
        // best effort
      }
    }
  }, []);

  const handleSandboxReady = () => {
    provisioningRef.current = false;
    setUser(guestUserRef.current);
  };

  // ?mode=demo auto-starts the guest sandbox flow once we know for sure there's no existing
  // signed-in session to redirect instead (auth.currentUser is only reliable after authChecked).
  useEffect(() => {
    if (!authChecked || auth.currentUser) return;
    if (!isDemoModeRequested()) return;
    if (demoAutoTriggeredRef.current) return;
    demoAutoTriggeredRef.current = true;
    handleTryGuest();
  }, [authChecked, handleTryGuest]);

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-200">
      <div className="flex min-h-screen w-full bg-base-100 shadow-panel overflow-hidden flex-col lg:flex-row">
        {/* Левая часть с текстом */}
        <div className="w-full lg:w-1/2 bg-base-300 text-base-content p-6 lg:p-8 flex flex-col justify-center">
          <div className="flex items-center">
            <img src="/logo.png" alt="Logo" className="h-10 w-10 lg:h-12 lg:w-12 mr-2" />
            <h1 className="text-2xl lg:text-4xl font-display">MFlow</h1>
          </div>
          <p className="text-xl lg:text-3xl mb-6">Delivery clarity for engineering teams</p>
          <p className="text-sm lg:text-md mb-6 opacity-80">
            Connect Jira once. Get PSI scores, risk flags, and AI that knows your team's data.
          </p>
        </div>

        {/* Правая часть с формой */}
        <div className="w-full lg:w-1/4 p-6 lg:p-8 m-auto flex flex-col items-center justify-center space-y-6">
          {guestPhase === 'provisioning' ? (
            guestSignedIn ? (
              <SandboxProvisioning triggered={sandboxTriggered} onReady={handleSandboxReady} />
            ) : (
              <div className="flex flex-col items-center text-center gap-4 py-10">
                <span className="loading loading-spinner loading-lg text-primary" />
                <h2 className="text-2xl font-bold font-display">Setting up your sandbox…</h2>
                <p className="opacity-60 max-w-xs">
                  We&apos;re spinning up a live demo project with real data so you can try MFlow right away.
                </p>
              </div>
            )
          ) : (
            <>
              <h2 className="text-2xl lg:text-3xl font-bold font-display text-center">Welcome back!</h2>
              <p className="text-base lg:text-lg opacity-60 text-center mb-6">
                Sign in to manage your projects with AI Project Manager
              </p>

              {/* Кнопка входа через Google */}
              <button onClick={handleGoogle} disabled={loading} className="btn btn-outline w-full">
                <FaGoogle className="mr-1 text-lg" />
                {loading ? 'Logging in...' : 'Sign in with Google'}
              </button>

              {/* Instant guest sandbox — no signup */}
              <button className="btn btn-primary shadow-glow w-full mt-4" onClick={handleTryGuest} disabled={loading}>
                <LuSparkles className="mr-1 text-lg" />
                Try it instantly — no signup
              </button>

              {/* Ошибка при авторизации */}
              {error && <p className="text-error text-center mt-4">{error}</p>}
              {guestError && <p className="text-error text-center mt-4">{guestError}</p>}

              <div className="text-center mt-2">
                <p className="opacity-60 text-sm lg:text-base">
                  Explore a live demo project for 30 minutes — no account required. Your sandbox data is deleted
                  automatically afterward.
                </p>
              </div>
              <div className="text-center mt-2">
                <p className="opacity-60 text-sm lg:text-base">
                  By signing in, you agree to our{' '}
                  <a
                    href="https://www.m-flow.io/terms-condition"
                    target="_blank"
                    className="link link-primary"
                    rel="noreferrer"
                  >
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.m-flow.io/private-policy"
                    target="_blank"
                    className="link link-primary"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
